/**
 * Conflict Group Service
 * Handles all API calls for Conflict Group management
 */

import myAxios from './axiositeachWrapper';
import Config from '../_config/config';

const BASE_URL = Config.nodeSiteUrl + 'v1/academic/timecraft-conflict-group';

export interface ConflictGroup {
  id: number;
  name: string;
  description?: string | null;
  scope: 'board' | 'global';
  boardId?: number | null;
  color?: string | null;
  createdDate?: string;
  createdBy?: number;
  isActive?: number;
  board?: {
    id: number;
    name: string;
  } | null;
  memberCount?: number;
  members?: ConflictGroupMember[];
}

export interface ConflictGroupMember {
  id: number;
  groupId: number;
  inputId: number;
  createdDate?: string;
  createdBy?: number;
  input?: {
    id: number;
    moduleCode: string;
    studentGroup: string;
    instructorName: string;
  };
}

export interface CreateConflictGroupData {
  name: string;
  description?: string;
  scope: 'board' | 'global';
  boardId?: number | null;
  color?: string;
}

export interface UpdateConflictGroupData {
  name?: string;
  description?: string;
  color?: string;
}

export interface ListConflictGroupsParams {
  boardId?: number;
  scope?: 'board' | 'global';
  includeMembers?: boolean;
}

class ConflictGroupService {
  /**
   * List all conflict groups
   */
  static async listGroups(params?: ListConflictGroupsParams) {
    const queryParams = new URLSearchParams();
    if (params?.boardId) queryParams.append('boardId', params.boardId.toString());
    if (params?.scope) queryParams.append('scope', params.scope);
    if (params?.includeMembers !== undefined) queryParams.append('includeMembers', params.includeMembers.toString());

    return myAxios.get(`${BASE_URL}?${queryParams.toString()}`);
  }

  /**
   * Get a single conflict group by ID
   */
  static async getGroup(id: number) {
    return myAxios.get(`${BASE_URL}/${id}`);
  }

  /**
   * Create a new conflict group
   */
  static async createGroup(data: CreateConflictGroupData) {
    return myAxios.post(BASE_URL, data);
  }

  /**
   * Update an existing conflict group
   */
  static async updateGroup(id: number, data: UpdateConflictGroupData) {
    return myAxios.put(`${BASE_URL}/${id}`, data);
  }

  /**
   * Delete a conflict group
   */
  static async deleteGroup(id: number) {
    return myAxios.delete(`${BASE_URL}/${id}`);
  }

  /**
   * Get members of a conflict group
   */
  static async getGroupMembers(groupId: number) {
    return myAxios.get(`${BASE_URL}/${groupId}/members`);
  }

  /**
   * Add a single member to a conflict group
   */
  static async addGroupMember(groupId: number, inputId: number) {
    return myAxios.post(`${BASE_URL}/${groupId}/members`, { inputId });
  }

  /**
   * Remove a single member from a conflict group
   */
  static async removeGroupMember(groupId: number, memberId: number) {
    return myAxios.delete(`${BASE_URL}/${groupId}/members/${memberId}`);
  }

  /**
   * Bulk add members to a conflict group
   */
  static async bulkAddMembers(groupId: number, inputIds: number[]) {
    return myAxios.post(`${BASE_URL}/${groupId}/members/bulk-add`, { inputIds });
  }

  /**
   * Bulk remove members from a conflict group
   */
  static async bulkRemoveMembers(groupId: number, inputIds: number[]) {
    return myAxios.delete(`${BASE_URL}/${groupId}/members/bulk-remove`, { data: { inputIds } });
  }

  /**
   * Get all groups that a specific input belongs to
   */
  static async getInputGroups(inputId: number, boardId?: number) {
    const params: ListConflictGroupsParams = { includeMembers: true };
    if (boardId) params.boardId = boardId;

    const response: any = await this.listGroups(params);
    if (response.data.success) {
      // Filter groups that contain this input
      return {
        ...response,
        data: {
          ...response.data,
          groups: response.data.groups.filter((group: any) =>
            group.members?.some((member: any) => member.inputId === inputId)
          )
        }
      };
    }
    return response;
  }
}

export default ConflictGroupService;
